import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "newModal", "editModal", "checklistContainer", "collectionCheckbox", "newCollectionName", "loadingState", "cardModal", "cardModalTitle", "cardModalContent"]

  connect() {
    // Initialize
  }

  // Open modal to add card to collections
  openModal(event) {
    event.preventDefault()
    const cardId = event.currentTarget.dataset.cardId
    this.modalTarget.dataset.cardId = cardId

    // Fetch current collections for this card and pre-check them
    this.fetchCardCollections(cardId)

    this.modalTarget.classList.remove("hidden")
  }

  // Open modal for creating new collection
  openNewCollectionModal(event) {
    if (event) event.preventDefault()

    if (this.hasNewModalTarget) {
      this.newModalTarget.classList.remove("hidden")
    } else {
      const modal = document.querySelector("#newCollectionModal")
      if (modal) {
        modal.classList.remove("hidden")
      }
    }
  }

  closeNewCollectionModal(event) {
    if (event && event.type !== "submit") {
      event.preventDefault()
    }

    if (this.hasNewModalTarget) {
      this.newModalTarget.classList.add("hidden")
    } else {
      const modal = document.querySelector("#newCollectionModal")
      if (modal) {
        modal.classList.add("hidden")
      }
    }
  }

  // Open modal for editing collection
  openEditModal(event) {
    event.preventDefault()
    const modal = document.querySelector("#editCollectionModal")
    if (modal) {
      modal.classList.remove("hidden")
    }
  }

  closeEditModal(event) {
    if (event) event.preventDefault()
    const modal = document.querySelector("#editCollectionModal")
    if (modal) {
      modal.classList.add("hidden")
    }
  }

  // Close modal
  closeModal(event) {
    if (event) event.preventDefault()
    this.modalTarget.classList.add("hidden")
    // Reset form
    if (this.hasNewCollectionNameTarget) {
      this.newCollectionNameTarget.value = ""
    }
    // Uncheck all checkboxes
    const checkboxes = this.modalTarget.querySelectorAll('input[type="checkbox"]')
    checkboxes.forEach(checkbox => {
      checkbox.checked = false
    })
  }

  // Prevent modal close when clicking inside
  preventClose(event) {
    event.stopPropagation()
  }

  // Close modal when clicking backdrop
  closeModalBackdrop(event) {
    if (event.target === event.currentTarget) {
      this.closeModal()
    }
  }

  // Fetch current collections for a card
  async fetchCardCollections(cardId) {
    // In a real implementation, you might fetch this via AJAX
    // For now, the backend will need to pass this info
  }

  // Submit selected collections
  async submitCollections(event) {
    event.preventDefault()

    const cardId = this.modalTarget.dataset.cardId
    const checkboxes = this.modalTarget.querySelectorAll('input[type="checkbox"]:checked')
    const collectionIds = Array.from(checkboxes).map(cb => cb.value)
    const newCollectionName = this.hasNewCollectionNameTarget ? this.newCollectionNameTarget.value.trim() : ""

    if (collectionIds.length === 0 && !newCollectionName) {
      alert("Please select at least one collection or create a new one.")
      return
    }

    // Show loading state
    if (this.hasLoadingStateTarget) {
      this.loadingStateTarget.classList.remove("hidden")
    }

    try {
      const response = await fetch("/add_to_collections", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
        },
        body: JSON.stringify({
          card_id: cardId,
          collection_ids: collectionIds,
          new_collection_name: newCollectionName
        })
      })

      if (response.ok) {
        const data = await response.json()

        // Show success message
        if (typeof Toast !== 'undefined') {
          Toast.fire({
            title: 'Added to collection(s)!',
            icon: 'success'
          })
        }

        // Close modal
        this.closeModal()

        // Reload page if on collections index to show updated counts
        if (window.location.pathname === "/collections") {
          window.location.reload()
        }
      } else {
        throw new Error("Failed to add to collections")
      }
    } catch (error) {
      console.error("Error:", error)
      alert("Failed to add card to collections. Please try again.")
    } finally {
      // Hide loading state
      if (this.hasLoadingStateTarget) {
        this.loadingStateTarget.classList.add("hidden")
      }
    }
  }

  // Handle checkbox selection changes
  updateSelection(event) {
    // Could be used to show visual feedback
  }

  // Open card modal (for expanded content)
  openCardModal(event) {
    event.preventDefault()

    if (!this.hasCardModalTarget) return

    const button = event.currentTarget
    const title = button.dataset.cardTitle || 'Card Details'
    const text = button.dataset.cardText

    // Set modal title
    if (this.hasCardModalTitleTarget) {
      this.cardModalTitleTarget.textContent = title
    }

    // Build and set modal content
    if (this.hasCardModalContentTarget && text) {
      const formattedContent = this.formatContent(text)
      this.cardModalContentTarget.innerHTML = formattedContent
    }

    // Show modal
    this.cardModalTarget.classList.remove('hidden')

    // Prevent body scroll
    document.body.style.overflow = 'hidden'
  }

  // Close the card modal
  closeCardModal(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (!this.hasCardModalTarget) return

    this.cardModalTarget.classList.add('hidden')

    // Restore body scroll
    document.body.style.overflow = ''
  }

  // Close card modal when clicking backdrop
  closeCardModalBackdrop(event) {
    if (event.target === event.currentTarget) {
      this.closeCardModal(event)
    }
  }

  // Format content to detect and properly display lists
  formatContent(text) {
    if (!text || text === 'undefined') return ''

    const lines = text.split('\n')
    let result = []
    let currentList = null
    let currentListType = null

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim()

      // Skip empty lines between list items
      if (!line && currentList) {
        continue
      }

      // Detect numbered list items (1., 2., 3., etc.)
      const numberedMatch = line.match(/^(\d+)\.\s+(.+)$/)
      if (numberedMatch) {
        if (currentListType !== 'ol') {
          if (currentList) {
            result.push(`</${currentListType}>`)
          }
          currentList = []
          currentListType = 'ol'
          result.push('<ol class="list-decimal list-inside space-y-1 ml-4">')
        }
        currentList.push(`<li class="text-gray-700 leading-relaxed">${numberedMatch[2]}</li>`)
        continue
      }

      // Detect bullet points (-, •, *)
      const bulletMatch = line.match(/^[-•*]\s+(.+)$/)
      if (bulletMatch) {
        if (currentListType !== 'ul') {
          if (currentList) {
            result.push(`</${currentListType}>`)
          }
          currentList = []
          currentListType = 'ul'
          result.push('<ul class="list-disc list-inside space-y-1 ml-4">')
        }
        currentList.push(`<li class="text-gray-700 leading-relaxed">${bulletMatch[1]}</li>`)
        continue
      }

      // Not a list item - close any open list
      if (currentList) {
        result.push(...currentList)
        result.push(`</${currentListType}>`)
        currentList = null
        currentListType = null
      }

      // Add regular paragraph (if not empty)
      if (line) {
        // Check if it's a header (ends with :)
        if (line.endsWith(':') && line.length < 50) {
          result.push(`<p class="text-gray-900 font-semibold mt-3 mb-1">${line}</p>`)
        } else {
          result.push(`<p class="text-gray-700 leading-relaxed">${line}</p>`)
        }
      }
    }

    // Close any remaining open list
    if (currentList) {
      result.push(...currentList)
      result.push(`</${currentListType}>`)
    }

    return result.join('')
  }
}
